import { useMemo } from "react";
import { useStore } from "@/stores/useStore";
import { useLayouts } from "@/hooks/useLayouts";
import { Card, Layout } from "@/types/v2";

export interface UseLayoutFilteringResult {
  layoutVisibleContainers: string[];
  applyLayoutFilter: (cards: Card[]) => Card[];
  selectedLayout: Layout[] | undefined;
  isLayoutSelected: boolean;
}

export interface UseLayoutFilteringProps {
  bypassLayout?: boolean;
}

export const useLayoutFiltering = (
  props?: UseLayoutFilteringProps
): UseLayoutFilteringResult => {
  const { bypassLayout = false } = props || {};
  const { dashboardLayoutId } = useStore();
  const { layouts } = useLayouts();

  // Get the current selected layout definition
  const selectedLayout = useMemo(() => {
    if (!dashboardLayoutId || !layouts || layouts.length === 0) {
      return undefined;
    }

    // Find the layout that contains the selected layout ID
    const layout = layouts.find((l) =>
      Object.keys(l).includes(dashboardLayoutId)
    );
    if (!layout) return undefined;

    return layout[dashboardLayoutId as keyof typeof layout];
  }, [dashboardLayoutId, layouts]);

  // Extract container IDs from the selected layout
  const layoutVisibleContainers = useMemo(() => {
    if (!selectedLayout) return [];
    return selectedLayout.map((layoutItem) => layoutItem.i);
  }, [selectedLayout]);

  // Function to filter cards by layout-visible containers
  const applyLayoutFilter = useMemo(
    () =>
      (cards: Card[]): Card[] => {
        // If bypassing layout filtering, return all cards
        if (bypassLayout) return cards;

        // If no layout is selected or no layout containers, return all cards
        if (layoutVisibleContainers.length === 0) return cards;

        // Filter cards to only include those in layout-visible containers
        return cards.filter(
          (card) =>
            card.currentContainer &&
            layoutVisibleContainers.includes(card.currentContainer)
        );
      },
    [layoutVisibleContainers, bypassLayout]
  );

  const isLayoutSelected = !!dashboardLayoutId && !!selectedLayout;

  return {
    layoutVisibleContainers,
    applyLayoutFilter,
    selectedLayout,
    isLayoutSelected,
  };
};
